<!--
  ReviewSearchFilter.vue
  후기 검색 및 필터링 컴포넌트
  
  주요 기능:
  - 검색 조건 및 검색어 입력
  - 조회 기간 설정
  - 평가 등급 필터링
  - 상태 필터링
  - 회사, 제조사, 카테고리, 진료과 필터링
-->
<template>
  <div class="container-wrapper">
    <div class="search-filter-container table-info-filter">
      <!-- 검색어 행 -->
      <div class="search-filter-row">
        <div class="search-filter-title">
          검색 조건
        </div>
        <div class="search-filter-content">
          <select class="search-select" v-model="searchFilter.filter">
            <option value="customerName">고객명</option>
            <option value="phoneNumber">전화번호</option>
            <option value="companyName">병원명</option>
            <option value="content">후기 내용</option>
          </select>
          <div class="search-input-container">
            <div class="search-icon">
              <i class="fa-regular fa-magnifying-glass" />
            </div>
            <input 
              type="text"
              class="search-input-text"
              v-model="searchFilter.keyword"
              @keyup.enter="handleSearch"
              placeholder="검색어를 입력하세요"
            />
          </div>
          <button
            class="search-btn"
            @click="handleSearch"
          >
            검색
          </button>
          <button
            class="init-btn"
            @click="handleReset"
          >
            초기화
          </button>
        </div>
      </div>

      <!-- 조회 기간 행 -->
      <div class="search-filter-row">
        <div class="search-filter-title">
          조회 기간
        </div>
        <div class="search-filter-content">
          <div class="date-filter-container">
            <div
              class="date-filter-item"
              :class="searchFilter.selectedDateFilter === 'all' ? 'selected' : ''"
              @click="handleDateFilter('all')"
            >
              전체
            </div>
            <div 
              v-for="item in searchFilter.dateFilters" 
              :key="item"
              class="date-filter-item"
              :class="searchFilter.selectedDateFilter === item ? 'selected' : ''"  
              @click="handleDateFilter(item)"
            >
              {{ item === 'etc' ? '기간 설정' : `최근 ${convertMonthAndYearText(item)}` }}
            </div>
          </div>
        </div>
      </div>
      <div class="date-selector-container custom" v-if="searchFilter.selectedDateFilter === 'etc'">
        <a-date-picker 
          style="width: 300px;"
          v-model:value="searchFilter.startAt" 
          type="date" 
          placeholder="시작 날짜를 선택해 주세요."
          :on-update:value="handleStartAt"
        />
        <div>~</div>
        <a-date-picker 
          style="width: 300px;"
          v-model:value="searchFilter.endAt" 
          type="date" 
          placeholder="종료 날짜를 선택해 주세요."
          :on-update:value="handleEndAt"
        />
        <div class="filter-desc">(등록 시각 기준)</div>
      </div>
    
      <!-- <div class="search-filter-content">
        <div class="filter-group">
          <label>진료과</label>
          <select 
            class="warranty-status-select"
            v-model="searchFilter.selectedDept"
            @change="handleSearch"
          >
            <option value="">전체</option>
            <option 
              v-for="item in deptList" 
              :value="item.code" 
              :key="item.code"
            >
              {{ item.name }}
            </option>
          </select>
        </div>
        <div class="filter-group">
          <label>제조사</label>
          <select 
            class="warranty-status-select"
            v-model="searchFilter.selectedManufacturer"
            @change="handleSearch"
          >
            <option value="">전체</option>
            <option 
              v-for="item in manufacturerList" 
              :value="item._id || item.id" 
              :key="item._id || item.id"
            >
              {{ item.name || item.manufacturerName }}
            </option>
          </select>
        </div>
        
        <div class="filter-group">
          <label>상태</label>
          <select 
            class="warranty-status-select"
            v-model="searchFilter.reviewStatus"
            @change="handleSearch"
          >
            <option value="">전체</option>
            <option value="exposed">노출중</option>
            <option value="review_requested">검수요청</option>
            <option value="hidden">비노출</option>
          </select>
        </div>
      </div> -->
    </div>
  </div>
</template>

<script>
import { convertMonthAndYearText } from '@/utils/utils';

export default {
  name: 'ReviewSearchFilter',
  props: {
    searchFilter: {
      type: Object,
      required: true
    },
    manufacturerList: {
      type: Array,
      default: () => []
    },
    categoryList: {
      type: Array,
      default: () => []
    },
    deptList: {
      type: Array,
      default: () => []
    }
  },
  emits: ['search', 'reset'],
  setup(props, { emit }) {
    // 검색 처리
    const handleSearch = () => {
      emit('search');
    };

    // 초기화 처리
    const handleReset = () => {
      emit('reset');
    };

    // 날짜 필터 처리
    const handleDateFilter = (value) => {
      if (value === props.searchFilter.selectedDateFilter) return;
      
      props.searchFilter.selectedDateFilter = value;
      
      if (value === 'all') {
        props.searchFilter.startAt = null;
        props.searchFilter.endAt = null;
      } else if (value !== 'etc') {
        props.searchFilter.startAt = getPastDatesInUnixTime(value);
        props.searchFilter.endAt = getCurrentUnixTime();
      }
      
      handleSearch();
    };

    // 시작 날짜 변경 처리
    const handleStartAt = (value) => {
      props.searchFilter.startAt = value;
      handleSearch();
    };

    // 종료 날짜 변경 처리
    const handleEndAt = (value) => {
      props.searchFilter.endAt = value;
      handleSearch();
    };

    // 현재 시간을 Unix 타임스탬프로 반환
    const getCurrentUnixTime = () => {
      const now = new Date();
      return Math.floor(now.getTime());
    };

    // 과거 날짜를 Unix 타임스탬프로 반환
    const getPastDatesInUnixTime = (value) => {
      const now = new Date();
      const pastDate = new Date(now);
      
      switch(value) {
        case 3:
          pastDate.setMonth(now.getMonth() - 3);
          break;
        case 12:
          pastDate.setMonth(now.getMonth() - 12);
          break;
        case 36:
          pastDate.setMonth(now.getMonth() - 36);
          break;
        default:
          return Math.floor(now.getTime());
      }
      
      return Math.floor(pastDate.getTime());
    };

    return {
      handleSearch,
      handleReset,
      handleDateFilter,
      handleStartAt,
      handleEndAt,
      convertMonthAndYearText
    };
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/certificates.scss';

.search-filter-container {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.search-filter-row {
  display: flex;
  align-items: flex-start;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.search-filter-title {
  min-width: 120px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.search-filter-content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.search-select, .warranty-status-select {
  min-width: 120px;
  height: 36px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 0 12px;
  font-size: 14px;
  background: #fff;
  
  &:focus {
    border-color: #FFCC32;
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 204, 50, 0.2);
  }
}

.search-input-container {
  position: relative;
  flex: 1;
  max-width: 300px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  z-index: 1;
}

.search-input-text {
  width: 100%;
  height: 36px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 0 12px 0 36px;
  font-size: 14px;
  
  &:focus {
    border-color: #FFCC32;
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 204, 50, 0.2);
  }
  
  &::placeholder {
    color: #bfbfbf;
  }
}

.search-btn, .init-btn {
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.search-btn {
  background: #FFCC32;
  color: #000;
  
  &:hover {
    background: #E6B82E;
  }
}

.init-btn {
  background: #f5f5f5;
  color: #666;
  
  &:hover {
    background: #e6e6e6;
  }
}

.date-filter-container {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.date-filter-item {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fff;
  
  &:hover {
    border-color: #FFCC32;
    color: #FFCC32;
  }
  
  &.selected {
    background: #FFCC32;
    border-color: #FFCC32;
    color: #000;
    font-weight: 500;
  }
}

.date-selector-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  width: 100%;
  
  &.custom {
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;
  }
}

.filter-desc {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .search-filter-row {
    flex-direction: column;
    gap: 8px;
  }
  
  .search-filter-title {
    min-width: auto;
    padding-top: 0;
  }
  
  .search-filter-content {
    width: 100%;
  }
  
  .search-input-container {
    max-width: none;
    /* 위(307줄)의 flex:1 은 flex-basis 를 0 으로 만든다.
       한 줄에 select·검색·초기화까지 같이 있어서 375px 에서는
       검색창이 35px 까지 찌그러지고 안의 input 이 21px 잘렸다.
       .search-filter-content 는 이미 flex-wrap:wrap 이므로
       기준폭을 100% 로 주면 검색창만 다음 줄로 내려가 온전히 보인다. */
    flex-basis: 100%;
  }

  .date-filter-container {
    width: 100%;
  }
  
  .date-filter-item {
    flex: 1;
    text-align: center;
    min-width: 80px;
  }
  
  .date-selector-container {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
}
</style> 